import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { switchMap } from 'rxjs';
import { LucideAngularModule } from 'lucide-angular';
import { Icons } from '../../../shared/helpers/icons';
import { CaseFilesService } from '../../../shared/services/caseFiles.service';
import { AssessCaseService } from '../../../shared/services/assessCase.service';
import { CASE_FILE_ACCEPT, CASE_FILE_ALLOWED_TYPES, CASE_FILE_MAX_TOTAL_SIZE_MB } from '../../../shared/models/caseFile';
import { SpinnerComponent } from '../spinner/spinner.component';

@Component({
  selector: 'upload-case',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    LucideAngularModule,
    SpinnerComponent
  ],
  templateUrl: './upload-case.component.html',
  styleUrl: './upload-case.component.css'
})
export class UploadCaseComponent {
  @Input() caseId!: string;
  @Input() buildingId!: string;
  @Output() onClose = new EventEmitter<void>();
  @Output() onCompleted = new EventEmitter<string>();

  icons = Icons;
  accept = CASE_FILE_ACCEPT;
  maxTotalSizeMb = CASE_FILE_MAX_TOTAL_SIZE_MB;
  selectedFiles: File[] = [];
  fullTeamRequired = new FormControl<number | null>(null, Validators.required);
  errorMessage: string | null = null;
  dragging: boolean = false;
  loading: boolean = false;

  constructor(
    private caseFilesService: CaseFilesService,
    private assessCaseService: AssessCaseService
  ) {}

  get totalSize(): number {
    return this.selectedFiles.reduce((sum, file) => sum + file.size, 0);
  }

  get isOverSizeLimit(): boolean {
    return this.totalSize > CASE_FILE_MAX_TOTAL_SIZE_MB * 1024 * 1024;
  }

  get canSave(): boolean {
    return this.selectedFiles.length > 0 && !this.isOverSizeLimit && this.fullTeamRequired.valid && !this.loading;
  }

  handleClose(): void {
    if (this.loading) return;
    this.onClose.emit();
  }

  handleDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragging = true;
  }

  handleDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.dragging = false;
  }

  handleDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragging = false;
    if (event.dataTransfer?.files) {
      this.addFiles(Array.from(event.dataTransfer.files));
    }
  }

  handleFileInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.addFiles(Array.from(input.files));
    }
    // reset so picking the same file again still fires change
    input.value = '';
  }

  addFiles(files: File[]): void {
    this.errorMessage = null;
    for (const file of files) {
      if (!CASE_FILE_ALLOWED_TYPES.includes(file.type)) {
        this.errorMessage = `${file.name} is not an allowed file type. Allowed: PDF, JPG, PNG, CSV, XLS.`;
        continue;
      }
      if (!this.selectedFiles.some(f => f.name === file.name && f.size === file.size)) {
        this.selectedFiles.push(file);
      }
    }
  }

  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);
  }

  formatSize(bytes: number): string {
    return bytes < 1024 * 1024 ? `${Math.ceil(bytes / 1024)} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  // the files are saved first, the case can only be closed once it has them
  saveAndClose(): void {
    if (!this.canSave) return;
    this.loading = true;
    this.errorMessage = null;
    this.caseFilesService.uploadCaseFiles(this.caseId, this.selectedFiles).pipe(
      switchMap(() => this.assessCaseService.completeUploadedCase({
        building_id: Number(this.buildingId),
        case_id: this.caseId,
        full_team_required: this.fullTeamRequired.value!
      }))
    ).subscribe({
      next: () => {
        this.loading = false;
        this.onCompleted.emit(this.caseId);
      },
      error: (error) => {
        this.loading = false;
        console.error('Error saving uploaded case:', error);
        this.errorMessage = error.message;
      }
    });
  }
}
